import React from 'react';
import * as PropTypes from 'prop-types';
import cx from 'classnames';
import useMedia from 'use-media';
import { ChevronRightIcon } from 'icons/solid';
import Link from '../../atoms/Link';
import Button from '../../atoms/Button';
import Badge from '../../atoms/Badge';
import Image from '../../atoms/Image';

const Title = ({ children, isH1, className }) => {
    return isH1 ? (
        <h1 className={className}>{children}</h1>
    ) : (
        <h2 className={className}>{children}</h2>
    );
};

const BannerContent = ({ isBlog, data, handleBtnClick }) => {
    const isMobile = useMedia({ maxWidth: 640 });
    const { button, alignment, background_color, text_color, title, is_h1, content } = data;

    let withButton;
    if (data.hasOwnProperty('button')) withButton = button.link && button.text;

    return (
        <div
            className={cx(
                'group relative h-auto w-full rounded-b-none rounded-tl-xl rounded-tr-xl bg-opacity-70 p-6',
                {
                    'bg-ui-white': background_color === 'yes',
                    'bg-transparent': background_color === 'no',
                    'sm:rounded-xl sm:py-8 sm:px-12': !isBlog,
                    'rounded-tl-xl bg-ui-white py-8 text-left shadow-up sm:w-[600px] sm:rounded-b-none sm:rounded-tr-xl sm:bg-opacity-100 sm:pl-12 sm:pr-16':
                        isBlog,
                    'sm:w-[600px] sm:text-center': alignment === 'center',
                    'sm:w-[600px] sm:text-left': alignment === 'left',
                    'sm:w-[600px] sm:text-right': alignment === 'right',
                    'text-center':
                        (alignment === 'center' && background_color === 'no') ||
                        (background_color !== 'no' && !isBlog),
                    'text-left': alignment === 'left' && background_color === 'no',
                    'text-right': alignment === 'right' && background_color === 'no',
                    'h-auto min-h-[120px]': isMobile,
                    'max-h-[280px] min-h-[148px]': !isMobile,
                }
            )}
            style={
                background_color !== 'yes' && background_color !== 'no'
                    ? { backgroundColor: background_color }
                    : {}
            }
        >
            <Title
                className={cx('mb-4 font-heading font-body-normal', {
                    'text-ui-black': !text_color,
                    'text-sliderH1': !isMobile,
                    'text-h2': isMobile,
                    'mr-2': isBlog && alignment === 'right',
                })}
                style={text_color ? { color: text_color } : {}}
                isH1={is_h1}
            >
                {title}
            </Title>
            {isBlog && button && button.link && (
                <ChevronRightIcon
                    className={cx(
                        'absolute w-[15px] transform duration-300 ease-out group-hover:translate-x-1',
                        {
                            'top-5 right-5': isMobile,
                            'top-10 right-10': !isMobile,
                        }
                    )}
                />
            )}
            <div
                className={cx('font-body font-body-regular', {
                    'text-ui-black': !text_color,
                    'mr-2': isBlog && alignment === 'right',
                })}
                style={text_color ? { color: text_color } : {}}
                dangerouslySetInnerHTML={{
                    __html: content,
                }}
            />
            {withButton && !isBlog && (
                <Button
                    size='md'
                    className={cx('duration-300', {
                        'cursor-pointer group-hover:shadow-down': withButton && !isMobile,
                        'bg-opacity-90': isMobile,
                    })}
                    onClick={handleBtnClick(button.link)}
                >
                    {button.text}
                </Button>
            )}
        </div>
    );
};

const Banner = ({ bannerInfo, handleBtnClick, isBlog }) => {
    const isMobile = useMedia({ maxWidth: 640 });
    const center = bannerInfo.alignment === 'CENTER' || bannerInfo.alignment === 'center';
    const left = bannerInfo.alignment === 'LEFT' || bannerInfo.alignment === 'left';
    const right = bannerInfo.alignment === 'RIGHT' || bannerInfo.alignment === 'right';
    const bannerClass = cx('w-full flex bg-cover bg-no-repeat bg-center relative h-[350px]', {
        'justify-center': center && !isMobile,
        'justify-start': left && !isMobile,
        'justify-end': right && !isMobile,
        'items-center px-wrapper': !isBlog && !isMobile,
        'w-full items-end': isBlog && !isMobile,
        'px-wrapper items-center': !isBlog && !isMobile,
        'justify-center items-end': isMobile && (center || left || right),
        'items-end': isMobile && isBlog && (center || left || right),
    });

    return (
        <div className={bannerClass}>
            <Image
                alt={bannerInfo.title}
                src={bannerInfo.image}
                layout='fill'
                objectFit='cover'
                objectPosition='center'
                quality={70}
            />

            {bannerInfo.hasOwnProperty('button') ? (
                <Link href={bannerInfo.button.link} className='pr-12 sm:pr-16' underline={false}>
                    <BannerContent
                        data={bannerInfo}
                        handleBtnClick={handleBtnClick}
                        isBlog={isBlog}
                    />
                </Link>
            ) : (
                <div>
                    <BannerContent
                        data={bannerInfo}
                        handleBtnClick={handleBtnClick}
                        isBlog={isBlog}
                    />
                </div>
            )}

            {bannerInfo.hasOwnProperty('link').text && !isBlog && (
                <Badge
                    className={cx('absolute right-0', {
                        'top-0 text-caption': isMobile,
                        'bottom-0': !isMobile,
                    })}
                    type={isMobile ? 'linkTop' : 'linkBottom'}
                >
                    {bannerInfo.link.text}
                </Badge>
            )}
        </div>
    );
};

Banner.propTypes = {
    bannerInfo: PropTypes.shape({
        title: PropTypes.string,
        data: PropTypes.object,
    }).isRequired,
    handleBtnClick: PropTypes.func,
};

export default Banner;
